import React, {useEffect, useMemo, useState} from 'react';
import {Form, Formik} from 'formik';
import * as Yup from 'yup';
import moment from 'moment';
import {useIntl} from 'react-intl';
import {useNavigate, useParams} from 'react-router-dom';
import {Box} from '@mui/material';

import {useAppDispatch, useAppSelector} from '../../../hooks/hooks';
import {getEmployee} from '../../../features/employees';
import {getAbsence, updateAbsence} from '../../../features/absences';
import {getAllAbsencesTypes} from '../../../features/types';
import {Absence} from '../../../features/absences/types';
import {Employee} from '../../../features/employees/types';
import {AbsenceBehavior, TiminatorAbsenceType} from '../../../features/types/types';
import {NAVIGATION_ROUTES} from '../../../routes/NavigationRoute';
import {Lx} from '../../../i18n/consts';
import BaseForm from '../../../components/shared/form-controls/base-form';
import { FormCheckbox } from '../../../components/shared/form-controls/form-checkbox';
import {FormTextarea} from '../../../components/shared/nextgen/form/form-textarea';
import FormDateSelector from '../../../components/shared/nextgen/form/form-date-selector';
import { FormNumberInput } from '../../../components/shared/nextgen/form/form-number-input';
import {
    canManageOldTimesheetItems,
    canManageTimesheetInternalNote,
    canManageTimesheetItems,
    canSeeTimesheetPage,
    canViewTimesheetInternalNote
} from '../../../features/profile/entityPermissions';
import { DropdownField } from '../../../components/shared/nextgen/dropdown-field';
import { REmployees } from '../../../features/roles/types';
import { setIndicatorLoading } from '../../../features/indicator';

interface EditForm {
    employee: REmployees;
    date: string;
    absenceType: TiminatorAbsenceType;
    hours: number;
    takeFromTimeBank: boolean;
    comment?: string;
    note?: string;
}

const isLimitToPlannedHoursAbsence = (absenceType: TiminatorAbsenceType | null | undefined): boolean => (
    absenceType?.behavior === AbsenceBehavior.LimitToPlannedHours
);

export default function EditAbsencePage() {
    const dispatch = useAppDispatch();
    const intl = useIntl();
    const navigate = useNavigate();
    const { absenceId } = useParams<{ absenceId: string }>();
    const profile = useAppSelector((selector) => selector.profile);
    const today = useMemo(() => moment(), []);
    const returnPath = NAVIGATION_ROUTES.TIMESHEET;
    const canViewSchedule = canSeeTimesheetPage(profile);
    const canManageOthers = canManageTimesheetItems(profile);
    const canManageOldEntries = canManageOldTimesheetItems(profile);
    const canEditInternalNote = canManageTimesheetInternalNote(profile);
    const canViewInternalNote = canViewTimesheetInternalNote(profile);
    const minEditableDate = useMemo(
        () => canManageOldEntries
            ? today.clone().add(-365, 'days').startOf('day')
            : today.clone().startOf('month'),
        [canManageOldEntries, today]
    );
    const maxEditableDate = useMemo(
        () => today.clone().startOf('day'),
        [today]
    );
    const isAllowedDate = (date?: string): boolean => {
        const parsedDate = moment(date, 'YYYY-MM-DD', true);

        return parsedDate.isValid()
            && !parsedDate.isBefore(minEditableDate, 'day')
            && !parsedDate.isAfter(maxEditableDate, 'day');
    };

    const absenceTypes = useAppSelector((selector) => selector.types.absences.items as TiminatorAbsenceType[]);
    const [absence, setAbsence] = useState<Absence | null>(null);
    const [employee, setEmployee] = useState<Employee | null>(null);
    const normalizeValue = (value?: string | null) => value?.trim().toLowerCase() ?? '';
    const isOwnEmployee = (targetEmployee: Employee) => (
        targetEmployee.id === profile.id
        || normalizeValue(targetEmployee.email) === normalizeValue(profile.email)
        || normalizeValue(`${targetEmployee.firstName} ${targetEmployee.lastName}`) === normalizeValue(profile.name)
    );

    useEffect(() => {
        if (!canViewSchedule) {
            navigate(NAVIGATION_ROUTES.MAIN, { replace: true });
        }
    }, [canViewSchedule, navigate]);

    useEffect(() => {
        if (absenceTypes.length === 0) {
            dispatch(getAllAbsencesTypes());
        }
    }, [dispatch, absenceTypes.length]);

    useEffect(() => {
        if (!absenceId) {
            navigate(returnPath);
            return;
        }
        dispatch(getAbsence(Number(absenceId))).unwrap().then((value) => {
            if (!isAllowedDate(value.date)) {
                navigate(returnPath, { replace: true });
                return;
            }
            setAbsence(value);
        }).catch(() => navigate(returnPath));
    }, [absenceId, dispatch, maxEditableDate, minEditableDate, navigate, returnPath]);

    useEffect(() => {
        if (!absence) {
            return;
        }
        dispatch(getEmployee(absence.employeeId)).unwrap().then((value) => {
            if (!canManageOthers && !isOwnEmployee(value)) {
                navigate(returnPath, { replace: true });
                return;
            }
            setEmployee(value);
        });
    }, [absence, canManageOthers, dispatch, navigate, profile.email, profile.id, profile.name, returnPath]);

    const initialValues = useMemo<EditForm | null>(() => {
        if (!absence || !employee || absenceTypes.length === 0) {
            return null;
        }
        const employeeOption: REmployees = {
            id: employee.id,
            name: `${employee.firstName} ${employee.lastName}`.trim(),
            isDefault: false,
        };

        return {
            employee: employeeOption,
            date: absence.date,
            hours: absence.hours,
            absenceType: absence.absenceType,
            takeFromTimeBank: absence.takeFromTimeBank ?? (absence.timeBankHours ?? 0) !== 0,
            comment: absence.comment,
            note: !absence.note ? '' : absence.note,
        };
    }, [absence, absenceTypes.length, employee]);

    const validationScheme: Yup.Schema<EditForm> = useMemo(() => Yup.object().shape({
        employee: Yup.mixed<REmployees>().required(),
        date: Yup.string().required().test('allowed-date', 'Invalid date', (value) => isAllowedDate(value)),
        absenceType: Yup.mixed<TiminatorAbsenceType>().required(),
        hours: Yup.number()
            .required()
            .min(0.5)
            .max(24)
            .test(
                'time-bank-whole-hours',
                'Time bank hours must be whole hours',
                (value, context) => {
                    const form = context.parent as EditForm;

                    if (!form.takeFromTimeBank) {
                        return true;
                    }

                    return typeof value === 'number' && Number.isInteger(value);
                }
            ),
        takeFromTimeBank: Yup.boolean().required(),
        comment: Yup.string().optional(),
        note: Yup.string().optional()
    }), [maxEditableDate, minEditableDate]);

    const save = async (values: EditForm) => {
        if (!absence || !employee) {
            return;
        }
        if (!canManageOthers && !isOwnEmployee(employee)) {
            return;
        }
        if (!isAllowedDate(values.date)) {
            return;
        }
        const editAbsence = {...absence};
        editAbsence.date = values.date;
        editAbsence.absenceType = absenceTypes.find((st) => st.id === values.absenceType.id) ?? editAbsence.absenceType;
        editAbsence.hours = values.hours;
        editAbsence.enteredHoursString = values.hours.toString();
        editAbsence.takeFromTimeBank = isLimitToPlannedHoursAbsence(editAbsence.absenceType)
            ? false
            : values.takeFromTimeBank;
        editAbsence.comment = values.comment ?? '';
        editAbsence.note = values.note ?? '';

        await dispatch(updateAbsence(editAbsence)).unwrap();
        navigate(returnPath);
    };

    const isLoading = canViewSchedule && (!initialValues || !absence || !employee || absenceTypes.length === 0);

    useEffect(() => {
        setIndicatorLoading(isLoading);

        return () => {
            setIndicatorLoading(false);
        };
    }, [isLoading]);

    if (!canViewSchedule || isLoading) {
        return null;
    }

    if (!initialValues || !absence || !employee) {
        return null;
    }

    const absenceTypeItems = absenceTypes.some((type) => type.id === initialValues.absenceType.id)
        ? absenceTypes
        : [initialValues.absenceType, ...absenceTypes];

    return (
        <Formik
            initialValues={initialValues}
            validationSchema={validationScheme}
            validateOnBlur={true}
            validateOnChange={true}
            enableReinitialize
            onSubmit={save}>
            {({ values, setFieldValue, setFieldTouched }) => {
                const touchHoursForTimeBankValidation = () => {
                    setFieldTouched('hours', true, true);
                };

                return (
                    <Form>
                        <BaseForm
                            contentGap={24}
                            formType='edit'
                            formHeader={Lx.Schedule.EDIT_TIMESHIFT_ABSENCE}
                            savingTitle={intl.formatMessage({ id: Lx.General.ABSENCE })}
                            onClose={() => navigate(returnPath)}
                            onAfterSave={() => navigate(returnPath)}
                        >
                            <div style={{ display: 'flex', flexDirection: 'column', gap: '24px', width: '100%', maxWidth: '500px' }}>
                                <DropdownField label={intl.formatMessage({ id: Lx.General.EMPLOYEE })}
                                    name={'employee'}
                                    required
                                    placeholder={intl.formatMessage({ id: Lx.General.CHOOSE_EMPLOYEE })}
                                    items={[initialValues.employee]}
                                    disabled/>
                                <Box
                                    display={'flex'}
                                    flexDirection={'row'}
                                    alignItems={'center'}
                                    gap={'24px'}
                                    justifyContent={'stretch'}
                                    sx={{ '& > *': { flex: '1 1 0', minWidth: 0 } }}>
                                    <FormDateSelector
                                        minDate={minEditableDate}
                                        maxDate={maxEditableDate}
                                        label={intl.formatMessage({ id: Lx.Schedule.TIMESHIFT_DATE })}
                                        name={'date'}
                                        required />
                                    <DropdownField label={intl.formatMessage({ id: Lx.General.ABSENCE_TYPE })}
                                        name={'absenceType'}
                                        placeholder={intl.formatMessage({ id: Lx.General.ABSENCE_TYPE })}
                                        items={absenceTypeItems}
                                        onChange={(item) => {
                                            if (isLimitToPlannedHoursAbsence(item)) {
                                                setFieldValue('takeFromTimeBank', false, false);
                                            }
                                        }}
                                        required/>
                                </Box>
                                <Box
                                    display={'flex'}
                                    flexDirection={'row'}
                                    alignItems={'flex-start'}
                                    gap={'24px'}
                                    sx={{ '& > *': { flex: '1 1 0', minWidth: 0 } }}>
                                    <FormNumberInput
                                        max={24}
                                        min={values.takeFromTimeBank ? 1 : 0.5}
                                        step={values.takeFromTimeBank ? 1 : 0.5}
                                        label={intl.formatMessage({ id: Lx.Schedule.TIMESHIFT_HOURS })} name={'hours'}
                                        placeholder={intl.formatMessage({ id: Lx.Schedule.TIMESHIFT_HOURS })} required/>
                                </Box>
                                {!isLimitToPlannedHoursAbsence(values.absenceType) && (
                                    <FormCheckbox
                                        name='takeFromTimeBank'
                                        label={intl.formatMessage({ id: Lx.Schedule.USE_FROM_TIME_BANK })}
                                        onChange={(event) => {
                                            touchHoursForTimeBankValidation();
                                            setFieldValue('takeFromTimeBank', event.target.checked, true);
                                        }}
                                        style={{ marginBottom: 0 }} />
                                )}
                                <FormTextarea label={intl.formatMessage({ id: Lx.Schedule.TIMESHIFT_NOTE })} name={'comment'}
                                    placeholder={intl.formatMessage({ id: Lx.Schedule.TIMESHIFT_NOTE })} resize={'vertical'}/>
                                {canViewInternalNote && (
                                    <FormTextarea label={intl.formatMessage({ id: Lx.Schedule.TIMESHIFT_INTERNAL_NOTE })} name={'note'}
                                        placeholder={intl.formatMessage({ id: Lx.Schedule.TIMESHIFT_INTERNAL_NOTE })}
                                        resize={'vertical'}
                                        disabled={!canEditInternalNote}/>
                                )}
                            </div>
                        </BaseForm>
                    </Form>
                );
            }}
        </Formik>
    );
}
